import { z } from "zod";

import {
  RoleMembershipTierSchema,
  RoleMemberTierSchema
} from "../../../../shared/domain/roles/role-membership.types";
import { RoleSchema } from "./core";

/**
 * Membership-tier schemas — who is in a Role, at which tier, and every Role one
 * caller belongs to.
 */

/** A user's ADMIN or MEMBER standing on a Role. OWNER never appears here — see RoleWithTierSchema. */
export const RoleMemberSchema = z.object({
  id: z.string(),
  roleId: z.string(),
  organizationId: z.string(),
  userId: z.string(),
  tier: RoleMemberTierSchema,
  addedByUserId: z.string().nullable(),
  createdAt: z.string()
});

export type RoleMember = z.infer<typeof RoleMemberSchema>;

/** "Who's in this Role" — owner composed from Role.ownerUserId, admins/members from their permission-set seats. */
export const RoleMembershipSchema = z.object({
  roleId: z.string(),
  ownerUserId: z.string().nullable(),
  admins: z.array(RoleMemberSchema),
  members: z.array(RoleMemberSchema)
});

export type RoleMembership = z.infer<typeof RoleMembershipSchema>;

export const RoleMemberUpsertBodySchema = z.object({
  userId: z.string().min(1),
  tier: RoleMemberTierSchema
});

export const RoleMemberPathVarsSchema = z.object({
  id: z.string().uuid(),
  userId: z.string()
});

/**
 * Removing a member is idempotent — `removed` says whether a seat in the Role's
 * `maintainer` or `member` permission set went. The owner, and a user seated only
 * in custom permission sets, answer `false` and lose nothing.
 */
export const RoleMemberRemoveResponseSchema = z.object({
  removed: z.boolean()
});

/** One Role a user belongs to, and how (owner/admin/member). */
export const RoleWithTierSchema = z.object({
  role: RoleSchema,
  tier: RoleMembershipTierSchema
});

export type RoleWithTier = z.infer<typeof RoleWithTierSchema>;

export const RolesMineResponseSchema = z.object({
  roles: z.array(RoleWithTierSchema)
});
